import { StringUtils } from '@/common/utils/StringUtils';

/**
 * EntityProvider scans the /entities/ directory in each module and builds several maps of
 * entity to component (ex: CTRT -> CtrtList) so that they can be picked up automatically
 * by EntityView.
 *
 * Note: Org-specific UIs are filtered out and must be inserted directly as props into the
 * EntityView of the org-specific route
 *
 * The filter of Object.keys(context) removes all org-specific UIs, but must add back OrgList, OrgDetail, and OrgForm (... || key.includes('/common/entities/org'))
 */
export class EntityProvider {
  // This notation allows EntityProvider to initialize the map of entity to ListComponent immediately
  // This is executed only once on startup
  // This is analagous to the clazz scan in the back end
  static listComponents = (() => {
    const components = {};
    const context = import.meta.glob('/src/*/entities/*/*List.jsx', { eager: true });
    Object.keys(context)
      .filter((key) => key.startsWith('/src/'))
      .filter((key) => !key.includes('/org/') || key.includes('/common/entities/org'))
      .forEach((key) => {
        const module = context[key];
        const componentName = key.split('/').pop().replace('.jsx', '');
        const exportKeys = Object.keys(module);
        if (exportKeys.length === 0) {
          console.warn(`No exports found in module ${key}`);
          return;
        }
        const component = module[exportKeys[0]];
        const entity = StringUtils.toCodeConvention(componentName.replace('List', ''));
        components[entity] = component;
      });
    return components;
  })();

  static detailComponents = (() => {
    const components = {};
    const context = import.meta.glob('/src/*/entities/*/*Detail.jsx', { eager: true });

    Object.keys(context)
      .filter((key) => key.startsWith('/src/'))
      .filter((key) => !key.includes('/org/') || key.includes('/common/entities/org'))
      .forEach((key) => {
        const module = context[key];
        const componentName = key.split('/').pop().replace('.jsx', '');
        const exportKeys = Object.keys(module);
        if (exportKeys.length === 0) {
          console.warn(`No exports found in module ${key}`);
          return;
        }
        const component = module[exportKeys[0]];
        const entity = StringUtils.toCodeConvention(componentName.replace('Detail', ''));
        components[entity] = component;
      });
    return components;
  })();

  static formComponents = (() => {
    const components = {};
    const context = import.meta.glob('/src/*/entities/*/*Form.jsx', { eager: true });
    Object.keys(context)
      .filter((key) => key.startsWith('/src/'))
      .filter((key) => !key.includes('/org/') || key.includes('/common/entities/org'))
      .forEach((key) => {
        const module = context[key];
        const componentName = key.split('/').pop().replace('.jsx', '');
        const exportKeys = Object.keys(module);
        if (exportKeys.length === 0) {
          console.warn(`No exports found in module ${key}`);
          return;
        }
        const component = module[exportKeys[0]];
        const entity = StringUtils.toCodeConvention(componentName.replace('Form', ''));
        components[entity] = component;
      });
    return components;
  })();

  static perspectiveComponents = (() => {
    const components = {};
    const context = import.meta.glob('/src/*/entities/*/*Perspective.jsx', { eager: true });
    Object.keys(context)
      .filter((key) => key.startsWith('/src/'))
      .filter((key) => !key.includes('/org/') || key.includes('/common/entities/org'))
      .forEach((key) => {
        const module = context[key];
        const componentName = key.split('/').pop().replace('.jsx', '');
        const exportKeys = Object.keys(module);
        if (exportKeys.length === 0) {
          console.warn(`No exports found in module ${key}`);
          return;
        }
        const component = module[exportKeys[0]];
        const entity = StringUtils.toCodeConvention(componentName.replace('Perspective', ''));
        components[entity] = component;
      });
    return components;
  })();

  // Returns the List component for the given entity
  static list(entity) {
    return EntityProvider.listComponents[entity];
  }

  // Returns the Detail component for the given entity
  static detail(entity) {
    return EntityProvider.detailComponents[entity];
  }

  // Returns the Form component for the given entity
  static form(entity) {
    return EntityProvider.formComponents[entity];
  }

  // Returns the Perspective component for the given entity
  static perspective(entity) {
    return EntityProvider.perspectiveComponents[entity];
  }
}
